"use client";

import { useMemo } from "react";
import { cn } from "../../lib/utils";
import ArthaLinkSmIcon from "../icons/arthalinkSmIcon";
import EditBrushIcon from "../icons/editBrushIcon";
import { ProfileAvatar } from "./profile-avatar";
import { ShareLinkRow } from "./share-link-row";

import type { ShareOverviewProps } from "../sharing/sharing.types";
export type {
  ShareMode,
  ShareJobData,
  ShareOverviewProps,
} from "../sharing/sharing.types";

export function ShareOverview({
  profileName,
  profileHandle,
  profileAvatar,
  shareLink,
  themeColor,
  linkedPlatforms,
  shareOptions,
  onCopyLink,
  copyDisabled,
  onEditSettings,
  onLinkedPlatformSelect,
  onShareOptionSelect,
  mode = "profile",
  jobData,
  hideEditButton,
  previewCard,
  platformsOnly,
  title,
  subtitle,
  tipNotice,
  belowShareLink,
}: ShareOverviewProps) {
  // Filter out share options that are already in linked platforms
  const filteredShareOptions = useMemo(() => {
    const linkedIds = new Set(linkedPlatforms.map((p) => p.id.toLowerCase()));
    return shareOptions.filter(
      (option) => !linkedIds.has(option.id.toLowerCase()),
    );
  }, [linkedPlatforms, shareOptions]);
  const visibleShareOptions = platformsOnly
    ? shareOptions
    : filteredShareOptions;

  return (
    <div className="flex flex-col gap-4 pb-3">
      {platformsOnly && (title || subtitle) && (
        <div className="px-1">
          {title && (
            <h2 className="text-lg font-semibold leading-6 text-[#101828]">
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="mt-1 text-sm leading-5 text-[#667085]">{subtitle}</p>
          )}
        </div>
      )}

      {!platformsOnly &&
        (previewCard ?? (
          <div
            className="relative rounded-lg border border-black px-4 pb-6 pt-6"
            style={{
              background: `linear-gradient(90deg, ${themeColor} 0%, ${themeColor} 100%)`,
            }}
          >
            {!hideEditButton && onEditSettings && (
              <button
                type="button"
                onClick={onEditSettings}
                className="absolute right-3 top-3 rounded-md border border-black bg-white p-1.5 text-[#344054] shadow-[0_2px_0_rgba(0,0,0,0.08)]"
                aria-label="Edit share settings"
              >
                <EditBrushIcon />
              </button>
            )}
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="flex h-[78px] w-[78px] items-center justify-center rounded-full">
                <ProfileAvatar
                  src={profileAvatar}
                  alt={profileName}
                  className="h-[70px] w-[70px] rounded-full"
                  fallbackClassName="bg-white/80"
                  logoClassName="h-[58%] w-[58%]"
                />
              </div>
              <div className="flex flex-col items-center gap-1">
                <p className="text-[20px] font-black uppercase leading-none text-black font-phudu">
                  {profileName}
                </p>
                <div className="flex items-center gap-1 text-sm text-[#58616F]">
                  <ArthaLinkSmIcon className="h-3.5 w-3.5" />
                  <span>{profileHandle}</span>
                </div>
              </div>
            </div>
          </div>
        ))}

      {!platformsOnly && (
        <div className="flex flex-col gap-1.5">
          <ShareLinkRow
            link={shareLink}
            onCopy={onCopyLink}
            disabled={copyDisabled}
          />
          <div className="flex flex-col gap-4 empty:hidden">
            {belowShareLink}
          </div>
        </div>
      )}

      {!platformsOnly && linkedPlatforms.length > 0 && (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-[#344054]">Linked Platforms</p>
          <div className="-mx-3 overflow-x-auto px-3">
            <div className="-mx-1 flex w-max gap-2 px-1 pb-1">
              {linkedPlatforms.map((platform) => {
                // All platforms are now clickable
                return (
                  <button
                    key={platform.id}
                    type="button"
                    aria-label={platform.label}
                    className="flex w-[63px] flex-col items-center gap-2 text-center transition-opacity hover:opacity-80"
                    onClick={() => {
                      onLinkedPlatformSelect?.(platform);
                      platform.onClick?.();
                    }}
                  >
                    <div className="flex h-[42px] w-[42px] items-center justify-center rounded-md bg-black">
                      {platform.icon}
                    </div>
                    <p className="text-[10px] leading-tight text-[#344054]">
                      {platform.label}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {platformsOnly && (
        <div className="mb-3.5 px-1">
          {tipNotice ?? (
            <div className="flex items-center gap-1.5 text-xs font-semibold text-[#ED5AA9]">
              <svg
                className="h-4 w-4 shrink-0 text-[#ED5AA9]"
                fill="none"
                height="24"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                width="24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1.3.5 2.6 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
                <path d="M9 18h6" />
                <path d="M10 22h4" />
              </svg>
              <span>Creators with artha.link in their bio earn 30% more.</span>
            </div>
          )}
        </div>
      )}

      {visibleShareOptions.length > 0 && (
        <>
          {!platformsOnly && <div className="h-px bg-[#D9D9D9]" />}
          <div
            className={cn(
              platformsOnly ? "px-1" : "-mx-3 overflow-x-auto px-3",
            )}
          >
            <div
              className={cn(
                platformsOnly
                  ? "grid grid-cols-4 gap-x-3 gap-y-4"
                  : "-mx-1 flex gap-2 px-1 pb-1 whitespace-nowrap",
              )}
            >
              {visibleShareOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  aria-label={option.label}
                  className={cn(
                    "flex flex-col items-center gap-1.5 text-center transition-opacity hover:opacity-80",
                    platformsOnly ? "w-full min-w-0" : "min-w-[63px]",
                  )}
                  onClick={() => {
                    if (onShareOptionSelect) {
                      onShareOptionSelect(option);
                    } else {
                      option.onClick?.();
                    }
                  }}
                >
                  <div
                    className={cn(
                      "flex h-[42px] w-[42px] items-center justify-center rounded-[5px]",
                      option.className,
                    )}
                  >
                    {option.icon}
                  </div>
                  <p className="text-[10px] leading-tight text-[#344054]">
                    {option.label}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
